/**
 * Custom wagmi transport that routes RPC calls through the backend proxy.
 *
 * This is necessary because:
 * 1. The Flare RPC endpoint requires an API key sent as an HTTP header (X-API-KEY)
 * 2. Browser-based wagmi/viem cannot add custom headers to RPC calls
 * 3. The backend /api/rpc endpoint acts as a proxy and adds the API key server-side
 *
 * Security benefit: API keys never get exposed to the browser/client-side code.
 */

import { custom } from 'viem';
import { BACKEND_URL } from './api';

// Public Flare RPC used as a direct fallback for receipt/block polling.
// These calls don't need the API key so we can bypass the backend proxy,
// which means tx confirmations are never blocked by backend availability.
const FLARE_PUBLIC_RPC = 'https://flare-api.flare.network/ext/C/rpc';

// Methods that only need block/receipt data — no API key required.
// Routing these directly prevents the backend proxy from becoming a
// single point of failure for transaction confirmation UX.
const DIRECT_RPC_METHODS = new Set([
  'eth_getTransactionReceipt',
  'eth_getTransactionByHash',
  'eth_blockNumber',
  'eth_getBlockByNumber',
  'eth_getBlockByHash',
]);

async function callDirect(method, params) {
  const res = await fetch(FLARE_PUBLIC_RPC, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ jsonrpc: '2.0', id: Date.now(), method, params }),
  });
  const data = await res.json();
  if (data.error) throw new Error(data.error.message || 'RPC error');
  return data.result;
}

export function createBackendProxyTransport() {
  // Validate backend URL configuration
  if (!BACKEND_URL || BACKEND_URL === 'undefined' || BACKEND_URL.includes('<')) {
    console.error('[customTransport] BACKEND_URL is not configured!');
    console.error('[customTransport] Please set VITE_BACKEND_URL environment variable');
    throw new Error('Backend URL not configured - check environment variables');
  }

  // Validate URL format
  if (!BACKEND_URL.startsWith('http://') && !BACKEND_URL.startsWith('https://')) {
    console.error('[customTransport] Invalid BACKEND_URL format');
    console.error('[customTransport] URL must start with http:// or https://');
    throw new Error('Invalid BACKEND_URL format');
  }

  // Only log backend URL in development
  if (import.meta.env.DEV) {
    console.log('[customTransport] Using backend RPC proxy:', `${BACKEND_URL}/api/rpc`);
  }

  return custom({
    async request({ method, params }) {
      // Receipt/block polling goes direct — never blocked by backend proxy
      if (DIRECT_RPC_METHODS.has(method)) {
        try {
          return await callDirect(method, params);
        } catch (err) {
          // Fallback to backend proxy if direct call fails
          console.debug('[customTransport] Direct RPC failed, falling back to proxy:', err.message);
        }
      }

      const rpcUrl = `${BACKEND_URL}/api/rpc`;

      try {
        // Only log RPC calls in development to reduce console noise
        if (import.meta.env.DEV) {
          console.debug(`[customTransport] RPC call: ${method}`, { params });
        }

        // Route all RPC calls through the backend proxy
        const response = await fetch(rpcUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            jsonrpc: '2.0',
            id: Date.now(),
            method,
            params,
          }),
        });

        // Enhanced error diagnostics
        if (!response.ok) {
          const errorText = await response.text();
          console.error('[customTransport] RPC request failed:', {
            status: response.status,
            statusText: response.statusText,
            url: rpcUrl,
            method,
            errorText,
          });

          // Provide specific error messages for common issues
          if (response.status === 404) {
            throw new Error('RPC endpoint not found. Check deployment.');
          } else if (response.status === 405) {
            throw new Error('Method not allowed. Backend is not accepting POST requests to /api/rpc. Check server configuration.');
          } else if (response.status === 502 || response.status === 503) {
            throw new Error(`Backend server unavailable (${response.status}). Please try again later.`);
          }

          throw new Error(`RPC request failed: ${response.status} ${response.statusText}`);
        }

        const data = await response.json();

        if (data.error) {
          const isExecutionReverted = data.error.code === -32000 &&
            (data.error.message?.includes('execution reverted') ||
             data.error.message?.includes('revert'));

          if (isExecutionReverted) {
            // Write calls (eth_estimateGas / eth_call) can revert for real reasons —
            // always preserve the full message so callers can surface it to the user.
            // Read calls that revert for expected reasons (no liquidity, etc.) will
            // also get the full message but their callers handle it silently.
            if (import.meta.env.DEV) {
              console.debug('[customTransport] Contract reverted:', method, data.error.message);
            }
            throw new Error(data.error.message || 'execution reverted');
          } else {
            console.error('[customTransport] RPC error:', data.error);
            throw new Error(data.error.message || 'RPC request failed');
          }
        }

        return data.result;
      } catch (error) {
        // Don't spam console with expected "execution reverted" errors
        const isExecutionReverted = error.message?.includes('execution reverted') ||
          error.message?.includes('revert');

        if (isExecutionReverted) {
          console.debug('[customTransport] Request reverted (expected):', method);
        } else {
          console.error('[customTransport] Request failed:', error);
        }

        // Re-throw with more context
        if (error instanceof TypeError && error.message.includes('fetch')) {
          throw new Error('Network error connecting to backend. Check your connection.');
        }

        throw error;
      }
    },
  });
}
